import type { MutableRefObject, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type { Nullable } from 'corgi-types';

import { MOBILE_VIEW_SCROLL_OFFSET, SCROLL_DIRECTIONS } from '../constants';
import { findScrollableAncestor, getScrollTop } from '../utils';

const LISTENING_EVENTS = ['scroll', 'resize'] as const;
type EventType = (typeof LISTENING_EVENTS)[number];

export type ScrollDirectionType = keyof typeof SCROLL_DIRECTIONS;

const useOnScrollChange = ({
  onDirectionChange,
  containerRef,
}: {
  onDirectionChange: (args: Nullable<ScrollDirectionType>) => void;
  containerRef: RefObject<HTMLElement | null>;
}) => {
  const prevScrollDirection = useRef<Nullable<ScrollDirectionType>>(null);
  const prevScrollTop = useRef(0);
  const dirChangePos = useRef<Nullable<number>>(null);

  const animationRefMap: Record<EventType, MutableRefObject<Nullable<number>>> = {
    scroll: useRef(null),
    resize: useRef(null),
  };

  useEffect(() => {
    const scrollable = findScrollableAncestor(containerRef.current);
    prevScrollTop.current = getScrollTop(scrollable);

    const listener = (eventType: EventType) => () => {
      const animationId = animationRefMap[eventType].current;
      if (animationId) {
        cancelAnimationFrame(animationId);
      }

      animationRefMap[eventType].current = requestAnimationFrame(() => {
        const scrollTop = getScrollTop(scrollable);
        const currentDirection =
          scrollTop < prevScrollTop.current
            ? SCROLL_DIRECTIONS.UP
            : scrollTop > prevScrollTop.current
              ? SCROLL_DIRECTIONS.DOWN
              : prevScrollDirection.current;

        if (currentDirection !== prevScrollDirection.current) {
          dirChangePos.current = scrollTop;
        }

        if (Math.abs(scrollTop - (dirChangePos.current || 0)) > MOBILE_VIEW_SCROLL_OFFSET) {
          onDirectionChange(currentDirection);
        }

        prevScrollDirection.current = currentDirection;
        prevScrollTop.current = scrollTop;
      });
    };

    const handlersMap: Record<PropertyKey, () => void> = {};
    LISTENING_EVENTS.forEach(eventType => {
      handlersMap[eventType] = listener(eventType);
      scrollable.addEventListener(eventType, handlersMap[eventType], { passive: true });
    });

    return () => {
      LISTENING_EVENTS.forEach(eventType => {
        scrollable.removeEventListener(eventType, handlersMap[eventType]);
        const animationId = animationRefMap[eventType].current;
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      });
    };
  }, [onDirectionChange, containerRef]);
};

export default useOnScrollChange;
